import * as React from 'react';

import { enumPurchaseOrderStatus } from '@rematter/frontend-lib-api-client';
import { cn } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useSetAtom } from 'jotai';

import { $updatePurchaseOrder } from '../../_state/purchaseOrderDetail.state';
import { NotesTab, type NotesConfig } from '../_shared/NotesTab';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { PurchaseOrderRecord } from '~/modules/trade/types/purchaseOrder';

type Props = {
  purchaseOrder: PurchaseOrderRecord;
  variant?: 'default' | 'embedded';
};

export const PurchaseOrderTabNotes: React.FC<Props> = ({
  purchaseOrder,
  variant = 'default',
}) => {
  const t = useTranslations();
  const updatePurchaseOrderMutation = useSetAtom($updatePurchaseOrder);

  const toast = useToast();

  const isReadonly =
    purchaseOrder.status === enumPurchaseOrderStatus.Closed ||
    purchaseOrder.status === enumPurchaseOrderStatus.Voided;

  const notesConfig: NotesConfig = {
    primaryTitle: t.purchaseOrderDetail.notes.general.title,
    primarySubtitle: t.purchaseOrderDetail.notes.general.subtitle,
    primaryPlaceholder: t.purchaseOrderDetail.notes.general.placeholder,
    secondaryTitle: t.purchaseOrderDetail.notes.completion.title,
    secondarySubtitle: t.purchaseOrderDetail.notes.completion.subtitle,
    secondaryPlaceholder: t.purchaseOrderDetail.notes.completion.placeholder,
    primaryField: 'notes',
    secondaryField: 'completionNotes',
  };

  const handleUpdateNotes = React.useCallback(
    async (updates: { [key: string]: string }) => {
      await updatePurchaseOrderMutation({
        id: purchaseOrder.id,
        ...updates,
      });

      toast.success(t.purchaseOrderDetail.notes.updated);
    },
    [purchaseOrder.id, updatePurchaseOrderMutation, toast, t],
  );

  return (
    <NotesTab
      entity={purchaseOrder}
      isReadonly={isReadonly}
      config={notesConfig}
      onUpdateNotes={handleUpdateNotes}
      className={cn(
        '@PurchaseOrderTabNotes',
        variant === 'embedded' && '[&>div]:p-0',
      )}
    />
  );
};
